import { useEffect, useState } from "react";
import { CTAGroup } from "@/components/CTAGroup";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { BRAND } from "@/data/site";

const HERO_SLIDES = [
  {
    src: "https://assets.cdn.filesafe.space/D8zHHsXQdNqiD1R03R0i/media/6a7b76924e002388f4b96eb9.jpg",
    alt: "Newly installed hardwood flooring in a Danbury, Connecticut living room",
  },
  {
    src: "https://assets.cdn.filesafe.space/D8zHHsXQdNqiD1R03R0i/media/6a7b7692699e0054a642b60f.jpeg",
    alt: "Luxury vinyl plank flooring installed in a Connecticut home",
  },
  {
    src: "https://assets.cdn.filesafe.space/D8zHHsXQdNqiD1R03R0i/media/6a7b7692746f53c63d36c1dd.webp",
    alt: "Refinished hardwood floors in a Fairfield County home",
  },
];

export function Hero() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = window.setInterval(
      () => setActive((i) => (i + 1) % HERO_SLIDES.length),
      6000,
    );
    return () => window.clearInterval(id);
  }, []);

  return (
    <section className="relative isolate overflow-hidden">
      <div className="absolute inset-0 -z-10">
        {HERO_SLIDES.map((slide, i) => (
          <img
            key={slide.src}
            src={slide.src}
            alt={slide.alt}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${i === active ? "opacity-100" : "opacity-0"}`}
            width={1920}
            height={1088}
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "low"}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-hero" />
      </div>
      <div className="container-page relative pt-10 md:pt-12 lg:pt-14 pb-24 md:pb-36 lg:pb-44 text-background">
        <p className="eyebrow mb-5 text-accent">Danbury, CT · Flooring Contractor</p>
        <h1 className="font-display font-bold leading-[1.05] max-w-3xl text-shadow-hero text-[50px] md:text-[56px]">
          Expert Flooring Installation & Renovation in Connecticut
        </h1>
        <p className="mt-4 max-w-2xl text-sm md:text-base text-background/95 leading-relaxed text-shadow-hero">
          Luxury vinyl tile, hardwood, laminate, floor refinishing and luxury vinyl plank installed by a licensed team with a decade of experience. Based in Danbury, CT — serving Fairfield, New Haven and Hartford County with premium materials, meticulous workmanship and personalized service on every job.
        </p>
        <div className="mt-7">
          <img
            src="https://assets.cdn.filesafe.space/D8zHHsXQdNqiD1R03R0i/media/6a7c60effe4291bd10b87b25.webp"
            alt="VRP Flooring certified seal"
            className="h-20 w-auto drop-shadow-lg"
            width={160}
            height={80}
            loading="eager"
          />
        </div>
        <div className="mt-6">
          <CTAGroup variant="onDark" />
        </div>
        <div className="mt-4">
          <Button
            asChild
            variant="outline"
            size="lg"
            className="bg-transparent border-background/40 text-background hover:bg-background hover:text-foreground font-semibold"
          >
            <a href="/__l5e/assets-v1/6424b902-fd72-4ef7-b453-fe369b3b22d0/Stock-flooring-catalog.pdf" target="_blank" rel="noopener noreferrer" download>
              <Download className="mr-1 h-4 w-4" /> Download our in-stock flooring catalog
            </a>
          </Button>
        </div>
        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-background/90 text-shadow-hero">
          <span>✓ Licensed &amp; Insured</span>
          <span>✓ 20+ Years in Business</span>
          <span>✓ 3000+ Satisfied Clients</span>
          <span>✓ 3000+ Completed Projects</span>
          <span>✓ Over 1000 verified reviews online</span>
        </div>
        <div className="mt-8 flex gap-2">
          {HERO_SLIDES.map((slide, i) => (
            <button
              key={slide.src}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Show hero image ${i + 1}`}
              aria-current={i === active}
              className={`h-1.5 rounded-full transition-all ${i === active ? "w-8 bg-accent" : "w-4 bg-background/40 hover:bg-background/70"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
